import React, { useEffect, useRef, useState } from "react";
import { Images } from "../../utils/Images";
import { Box, Typography, useMediaQuery, Modal, IconButton, Button, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CampaignButtonNoArrowVariant } from "../../components/common/Button/Button";
import { getCampaignById } from "../../api/services/campaignService";
import { getCampaignUrl } from "../../utils/slugUtils";
import { isTempleCampaign } from "../../utils/templeCampaign";
import TempleFailure from "./TempleFailure";
import { makeDonationAction } from "../../store/slices/donationSlice";
import { createReminder } from "../../api/services/reminderService";
import CloseIcon from "@mui/icons-material/Close";
import DonateBoxIcon from "../../assets/Icons/DonateBox.svg";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  sanitizeFormFieldValue,
  validateIndianMobile,
} from "../../utils/phoneValidation";

/** Figma 9962:22898 — input row: px 16 py 8, inner content h 36, border #DDE2E5, r 8 */
const reminderFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    padding: "8px 14px",
    minHeight: "46px",
    boxSizing: "border-box",
    alignItems: "center",
    "& fieldset": { borderColor: "#DDE2E5" },
    "&:hover fieldset": { borderColor: "#DDE2E5" },
    "&.Mui-focused fieldset": { borderColor: "#DDE2E5", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    fontFamily: '"Red Hat Display", sans-serif',
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "18px",
    color: "#141414",
    padding: 0,
    height: "30px",
    boxSizing: "border-box",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#979796",
    opacity: 1,
  },
};

const PaymentFailurePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Get payment data from navigation state
  const paymentState = location.state || {};
  const paymentData = paymentState.data || {};
  const paymentDetails = paymentState.details || {};
  const status = paymentState.status || "Failed";

  const donationProcessedRef = useRef(false);
  const allowBrowserBackRef = useRef(false);
  const [showBackPopup, setShowBackPopup] = useState(false);
  const [popupStep, setPopupStep] = useState("initial");
  const [reminderForm, setReminderForm] = useState({
    fullName: "",
    contactNumber: "",
  });
  const [reminderDate, setReminderDate] = useState(null);
  const [isReminderSubmitting, setIsReminderSubmitting] = useState(false);

  // Process donation record for failed payments (for tracking purposes)
  useEffect(() => {
    // Early return if conditions aren't met or already processed
    if (!paymentData || !paymentDetails || donationProcessedRef.current) {
      return;
    }

    // Extract transaction ID from different payment methods
    const transactionId =
      paymentData.razorpay_payment_id ||
      paymentData.paymentId ||
      paymentData.capture?.purchase_units?.[0]?.payments?.captures?.[0]?.id ||
      paymentData.sessionId ||
      null;

    // Extract amount from different payment methods
    const donationAmount =
      paymentData.amount ||
      paymentDetails.amount ||
      paymentData.capture?.purchase_units?.[0]?.payments?.captures?.[0]?.amount?.value ||
      0;

    // Determine payment type from details
    const paymentType = paymentDetails.paymentType || 
                       (paymentDetails.campaignName ? 'campaign' : 'donation') ||
                       'donation';

    // Determine if overseas payment
    const isOverseas = paymentDetails.donationType === "overseas" || 
                      paymentDetails.currency !== "INR" ||
                      paymentData.currency !== "INR";

    // Extract mobile number from all possible locations
    const mobileNumber = 
      paymentDetails.phone || 
      paymentDetails.phoneNumber || 
      paymentDetails.mobile || 
      paymentDetails.contact || 
      paymentDetails.contactNumber ||
      paymentData.userDetails?.phone ||
      paymentData.userDetails?.phoneNumber ||
      paymentData.userDetails?.mobile ||
      paymentData.userDetails?.contact ||
      paymentData.contact ||
      paymentData.phoneNumber ||
      paymentData.phone ||
      paymentData.mobile ||
      '';

    const donationDatas = {
      userId: paymentDetails.userId || user?.id || null,
      donation_cause: paymentType === 'campaign' ? paymentDetails.campaignName : null,
      transaction_id: transactionId,
      amount: donationAmount,
      paymentType: paymentType,
      animalId: paymentDetails.adoptanimalId || paymentDetails.animalId || null,
      over_seas: isOverseas ? 1 : 0,
      status: 0, // Failed payment
      fullName: paymentDetails.fullName || paymentDetails.name || paymentData.name || '',
      email: paymentDetails.email || paymentData.email || '',
      mobileNumber: mobileNumber
    };

    const processDonation = async () => {
      try {
        // Mark as processing to prevent duplicate calls
        donationProcessedRef.current = true;
        // Make donation API call (with status 0 for failed)
        await dispatch(makeDonationAction(donationDatas)).unwrap();
      } catch (error) {
        console.error('Donation processing error:', error);
        // Don't block the UI if donation recording fails
        // Keep the ref as true to prevent retry
      }
    };

    processDonation();
  }, [paymentData, paymentDetails, dispatch, user]);

  // Intercept browser back only on failure page and show retry popup.
  useEffect(() => {
    const markerState = {
      ...(window.history.state || {}),
      __paymentFailureGuard: true,
    };
    window.history.pushState(markerState, "");

    const onPopState = () => {
      if (allowBrowserBackRef.current) {
        return;
      }
      const keepPageState = {
        ...(window.history.state || {}),
        __paymentFailureGuard: true,
      };
      window.history.pushState(keepPageState, "");
      setShowBackPopup(true);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Format currency amount
  const formatAmount = (amount, currency = "INR") => {
    if (!amount) return "₹0";
    const currencySymbols = {
      INR: "₹",
      USD: "$",
      AED: "د.إ",
      CAD: "C$",
      AUD: "A$",
      GBP: "£",
    };
    const symbol = currencySymbols[currency] || currency;
    if (currency === "INR") {
      return `${symbol}${Number(amount).toLocaleString("en-IN")}`;
    }
    return `${symbol}${Number(amount).toLocaleString()}`;
  };

  // Extract transaction details
  const transactionId =
    paymentData.razorpay_payment_id ||
    paymentData.paymentId ||
    paymentData.sessionId ||
    "N/A";
  const paymentDateTime =
    paymentData.paymentDateTime ||
    (paymentData.formattedDate && paymentData.formattedTime
      ? `${paymentData.formattedDate} | ${paymentData.formattedTime}`
      : new Date().toLocaleString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }));
  const paymentMethod =
    paymentData.paymentMethod || "UPI (Unified Payments Interface)";
  const amount = paymentData.amount || paymentDetails.amount || 0;
  const currency = paymentData.currency || paymentDetails.currency || "INR";
  const failedCampaignName =
    paymentDetails?.vendorName ||
    paymentDetails?.vendor_name ||
    paymentData?.vendorName ||
    paymentData?.vendor_name ||
    paymentDetails?.campaignName ||
    paymentDetails?.campaignTitle ||
    paymentData?.campaignName ||
    paymentData?.campaignTitle ||
    "this campaign";
  const isVendorDonation =
    String(paymentDetails?.donationType || paymentData?.donationType || "").toLowerCase() === "vendor";
  const failedDonationType = String(paymentDetails?.donationType || paymentData?.donationType || "").toLowerCase();
  const isMonthlyDonation = failedDonationType === "monthly";
  const isOverseasDonation = failedDonationType === "overseas";
  const isProductDonation =
    Array.isArray(paymentDetails?.selectedProducts) ||
    Array.isArray(paymentData?.selectedProducts) ||
    String(paymentDetails?.paymentType || paymentData?.paymentType || "").toLowerCase() === "product";
  const failedCampaignId =
    paymentDetails?.campaignId ||
    paymentDetails?.campaign_id ||
    paymentData?.campaignId ||
    paymentData?.campaign_id ||
    null;
  const failedVendorId =
    paymentDetails?.vendorId ||
    paymentDetails?.vendor_id ||
    paymentData?.vendorId ||
    paymentData?.vendor_id ||
    null;

  // Temple payments get their own failure page. Paid from a temple donation box → the
  // thankYouVariant flag (homepage gifts carry no campaign); otherwise look the campaign up once,
  // showing nothing until we know rather than flashing the regular page.
  const isTempleGift = paymentDetails.thankYouVariant === "temple";
  const templeLookupId = paymentDetails.sourceCampaignId || failedCampaignId || null;
  const [templeCampaign, setTempleCampaign] = useState(undefined); // undefined = checking, null = not a temple
  useEffect(() => {
    if (!templeLookupId) {
      setTempleCampaign(null);
      return undefined;
    }
    let cancelled = false;
    getCampaignById(templeLookupId)
      .then((res) => !cancelled && setTempleCampaign(isTempleGift || isTempleCampaign(res?.data) ? res.data || null : null))
      .catch(() => !cancelled && setTempleCampaign(null));
    return () => {
      cancelled = true;
    };
  }, [templeLookupId, isTempleGift]);
  const showTemplePage = isTempleGift || Boolean(templeCampaign);

  // Retry from where the donor paid: the temple's page, or /temples for the temples-page box.
  const retryTemplePayment = () => {
    allowBrowserBackRef.current = true;
    setShowBackPopup(false);
    if (templeCampaign || templeLookupId) {
      navigate((templeCampaign && getCampaignUrl(templeCampaign)) || `/campaign/${templeLookupId}`);
      return;
    }
    navigate("/temples");
  };

  // Handle go to home button click
  const handleGoToHome = () => {
    navigate("/");
  };

  const handleCloseBackPopup = () => {
    if (popupStep === "success") {
      setShowBackPopup(false);
      setPopupStep("initial");
      setReminderDate(null);
      setReminderForm({ fullName: "", contactNumber: "" });
      allowBrowserBackRef.current = true;
      navigate("/");
      return;
    }
    setShowBackPopup(false);
    setPopupStep("initial");
    setReminderDate(null);
    setReminderForm({ fullName: "", contactNumber: "" });
  };

  const handleReminderSuccessClose = () => {
    setShowBackPopup(false);
    setPopupStep("initial");
    setReminderDate(null);
    setReminderForm({ fullName: "", contactNumber: "" });
    allowBrowserBackRef.current = true;
    navigate("/");
  };

  const handleDonateLater = () => {
    setPopupStep("reminder");
  };

  const handleDonateNow = () => {
    if (showTemplePage) {
      retryTemplePayment();
      return;
    }
    setShowBackPopup(false);
    if (isVendorDonation && failedVendorId) {
      navigate(`/small-vendors/payment/${failedVendorId}`);
      return;
    }
    if (isProductDonation) {
      navigate("/donate-product-page", {
        state: {
          campaignTitle: failedCampaignName || "",
          campaignId: failedCampaignId || null,
          amount: amount || 1000,
          baseAmount: amount || 1000,
          currency: currency || "INR",
        },
      });
      return;
    }
    if (isMonthlyDonation) {
      const amountParam = Number(amount) || 1000;
      navigate(`/donate-monthly-page?amount=${encodeURIComponent(String(amountParam))}`);
      return;
    }
    if (isOverseasDonation) {
      navigate("/overseas-donor-page", {
        state: {
          campaignTitle: failedCampaignName || "",
          campaignId: failedCampaignId || null,
          amount: amount || 1000,
          currency: currency || "USD",
        },
      });
      return;
    }
    navigate("/donate-once-page", {
      state: {
        campaignTitle: failedCampaignName || "",
        campaignId: failedCampaignId || null,
        amount: amount || 1000,
      },
    });
  };

  const handleReminderInputChange = (field) => (event) => {
    const value = sanitizeFormFieldValue(field, event.target.value);
    setReminderForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSetReminder = async () => {
    const name = (reminderForm.fullName || "").trim();
    const phoneCheck = validateIndianMobile(reminderForm.contactNumber);
    const email =
      (paymentDetails?.email || paymentData?.email || user?.email || "").trim();
    const reminderDateValue = reminderDate?.format("YYYY-MM-DD");
    const reminderTime = "09:30";

    if (!name) {
      alert("Please enter full name.");
      return;
    }
    if (!phoneCheck.isValid) {
      alert(phoneCheck.error);
      return;
    }
    const mobile = phoneCheck.digits;
    if (!reminderDateValue) {
      alert("Please select reminder date.");
      return;
    }
    if (!email) {
      alert("Email is required to set reminder.");
      return;
    }

    try {
      setIsReminderSubmitting(true);
      const reminderPayload = {
        name,
        email,
        mobile,
        reminder_date: reminderDateValue,
        reminder_time: reminderTime,
      };

      if (isVendorDonation) {
        reminderPayload.vendor_id = failedVendorId;
        reminderPayload.vendor_name = failedCampaignName;
      } else {
        reminderPayload.campaign_name = failedCampaignName;
        reminderPayload.campaign_id = failedCampaignId;
      }

      await createReminder(reminderPayload);

      setPopupStep("success");
      setReminderDate(null);
      setReminderForm({ fullName: "", contactNumber: "" });
    } catch (error) {
      console.error("Failed to set reminder:", error);
      alert(error?.message || "Failed to set reminder. Please try again.");
    } finally {
      setIsReminderSubmitting(false);
    }
  };

  const methodKey = String(paymentData.paymentMethod || "").toLowerCase();
  const templeMethodLabel =
    { upi: "UPI", card: "Card", netbanking: "Net banking", wallet: "Wallet", emi: "EMI" }[methodKey] ||
    (methodKey && methodKey !== "online" ? paymentData.paymentMethod : "Online");

  return (
    <>
    {templeLookupId && templeCampaign === undefined && !isTempleGift ? (
      <Box sx={{ minHeight: "70vh" }} />
    ) : showTemplePage ? (
      <TempleFailure
        title={templeCampaign?.title || paymentDetails.campaignName || paymentDetails.campaignTitle}
        amount={amount}
        tipAmount={paymentDetails.tipAmount || paymentData.tipAmount || 0}
        currency={currency}
        donorName={paymentDetails.fullName || paymentDetails.name || paymentData.name || user?.name || ""}
        dateLabel={new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
        reference={paymentData.razorpay_payment_id || paymentData.paymentId || paymentData.orderId || paymentDetails.subscriptionId || ""}
        paymentMethod={templeMethodLabel}
        isMonthly={isMonthlyDonation}
        campaignId={templeLookupId}
        onRetry={retryTemplePayment}
        onSupport={() => {
          allowBrowserBackRef.current = true;
          navigate("/contact");
        }}
      />
    ) : (
    <Box
      sx={{
        width: "94.44%",
        maxWidth: "1440px",
        margin: "0 auto",
        paddingTop: isMobile ? "20px" : "40px",
      }}
    >
      {/* Receipt Card Section - No Banner */}
      <Box
        sx={{
          maxWidth: "760px",
          margin: "0 auto",
          background: "#FFFFFF",
          borderRadius: isMobile ? "16px" : "34px",
          boxShadow: "0 11.566px 34.697px 0 rgba(170, 170, 170, 0.25), 0 4px 12px 0 rgba(0, 0, 0, 0.15)",
          padding: isMobile ? "16px" : "34px",
          marginTop: isMobile ? "10px" : "40px",
          marginBottom: isMobile ? "20px" : "40px",
          width: "100%",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "row" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "flex-start",
            marginBottom: "32px",
            borderBottom: "1px solid #E6E6E6",
            paddingBottom: "24px",
            gap: isMobile ? "16px" : "0",
          }}
        >
          <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <Box>
              <img
                src={Images.failureIcon}
                alt="Payment Failed"
                style={{
                  width: isMobile ? "30px" : "69px",
                  height: isMobile ? "30px" : "69px",
                }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "15px",
                  fontWeight: 400,
                  color: "#4A4A49",
                  marginBottom: isMobile ? "4px" : "12px",
                }}
              >
                Payment Failed
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "16px" : "24px",
                  fontWeight: 700,
                  color: "#1D1D1B",
                }}
              >
                {formatAmount(amount, currency)}
              </Typography>
            </Box>
          </Box>
          <CampaignButtonNoArrowVariant
            text="Go to Home"
            isMobile={isMobile}
            onClick={handleGoToHome}
          />
        </Box>

        {/* Transaction Details */}
        <Box
          sx={{
            display: isMobile ? "flex" : "grid",
            flexDirection: isMobile ? "column" : "unset",
            gridTemplateColumns: isMobile ? "none" : "1fr 1fr",
            gap: isMobile ? "12px" : "24px",
          }}
        >
          {/* Left Column */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? "8px" : "20px",
              textAlign: "left",
            }}
          >
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Transaction ID
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1D1D1B",
                    textAlign: "right",
                  }}
                >
                  {transactionId}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Payment Date & Time
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1D1D1B",
                    textAlign: "right",
                    flex: 1,
                    ml: 1,
                  }}
                >
                  {paymentDateTime}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Payment Method
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1D1D1B",
                    textAlign: "right",
                    flex: 1,
                    ml: 1,
                  }}
                >
                  {paymentMethod}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Payment Status
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#F04951",
                    textAlign: "right",
                  }}
                >
                  {status}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Total Amount
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1D1D1B",
                    textAlign: "right",
                  }}
                >
                  {formatAmount(amount, currency)}
                </Typography>
              )}
            </Box>
          </Box>
          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                textAlign: "right",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                }}
              >
                {transactionId}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                }}
              >
                {paymentDateTime}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                }}
              >
                {paymentMethod}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#F04951",
                }}
              >
                {status}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                }}
              >
                {formatAmount(amount, currency)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

    </Box>
    )}

      <Modal open={showBackPopup} onClose={handleCloseBackPopup}>
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: isMobile ? 2 : 3,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box
            sx={{
              width: "100%",
              maxWidth:
                popupStep === "initial" ? "482px" : popupStep === "reminder" ? "500px" : "430px",
              bgcolor: "#FFFFFF",
              borderRadius: popupStep === "initial" ? "16px" : "14px",
              p:
                isMobile
                  ? "16px 14px"
                  : popupStep === "initial"
                    ? "30px"
                    : popupStep === "success"
                      ? "24px 20px"
                      : "24px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: isMobile ? "14px" : popupStep === "initial" ? "32px" : "14px",
              position: "relative",
            }}
          >
            <IconButton
              onClick={handleCloseBackPopup}
              sx={{
                position: "absolute",
                right: isMobile ? 6 : popupStep === "initial" ? 10 : 10,
                top: isMobile ? 6 : popupStep === "initial" ? 10 : 10,
                width: popupStep === "reminder" && !isMobile ? "28.685px" : undefined,
                height: popupStep === "reminder" && !isMobile ? "28.685px" : undefined,
                padding: popupStep === "reminder" && !isMobile ? 0 : undefined,
              }}
              aria-label="Close"
            >
              <CloseIcon
                sx={{
                  color: "#000000",
                  fontSize:
                    isMobile ? 20 : popupStep === "initial" ? 24 : 22,
                }}
              />
            </IconButton>

            {popupStep === "initial" ? (
              <>
                <Box
                  component="img"
                  src={DonateBoxIcon}
                  alt="Donation box"
                  sx={{ width: isMobile ? "120px" : "160px", height: "auto", mt: 1 }}
                />

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: isMobile ? "22px" : "24px",
                      fontWeight: 700,
                      lineHeight: isMobile ? "24px" : "24px",
                      color: "#05072D",
                      letterSpacing: isMobile ? "-0.4px" : "-0.72px",
                      mb: isMobile ? 1 : 2.25,
                    }}
                  >
                    Please Don&apos;t Give Up
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: isMobile ? "14px" : "16px",
                      lineHeight: isMobile ? "22px" : "26px",
                      fontWeight: 500,
                      color: "#686866",
                      letterSpacing: isMobile ? "-0.2px" : "-0.32px",
                      maxWidth: "419px",
                      mx: "auto",
                    }}
                  >
                    &ldquo;We promise your contribution will make a positive change by supporting the noble mission
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    gap: isMobile ? 1.5 : "20px",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  <Button
                    fullWidth
                    onClick={handleDonateLater}
                    sx={{
                      height: "53px",
                      borderRadius: "10px",
                      bgcolor: "#1D1D1B",
                      color: "#FFFFFF",
                      boxShadow: "0px 4px 0px #B9B9B8",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: "14px",
                      "&:hover": { bgcolor: "#2A2A28" },
                    }}
                  >
                    Donate Later
                  </Button>
                  <Button
                    fullWidth
                    onClick={handleDonateNow}
                    sx={{
                      height: "53px",
                      borderRadius: "10px",
                      bgcolor: "#F04951",
                      color: "#FFFFFF",
                      boxShadow: "0px 4px 0px #141414",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: "14px",
                      "&:hover": { bgcolor: "#D63F46" },
                    }}
                  >
                    Donate Now
                  </Button>
                </Box>
              </>
            ) : popupStep === "reminder" ? (
              <>
                {/* Reminder step — matches Figma 9962:22898 */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: isMobile ? "12px" : "14px",
                    pt: isMobile ? 0 : "4px",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: 700,
                      lineHeight: isMobile ? "24px" : "24px",
                      color: "#0a0a23",
                      letterSpacing: "-0.6px",
                      textAlign: "center",
                    }}
                  >
                    Thankyou for Caring
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: isMobile ? "13px" : "14px",
                      lineHeight: isMobile ? "20px" : "22px",
                      fontWeight: 500,
                      color: "#686866",
                      letterSpacing: "-0.2px",
                      maxWidth: "430px",
                      width: "100%",
                      mx: "auto",
                      textAlign: "center",
                      textTransform: "capitalize",
                    }}
                  >
                    We understand timing matters. We&apos;ll save your intent to give and remind you later — so when
                    you&apos;re ready, you can still make a difference for {failedCampaignName}. Every reminder = A
                    meal, safe bed, or a smile
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "430px",
                    border: "1px solid #F1F1F1",
                    borderRadius: "20px",
                    boxShadow: "0px 45px 13px 0 rgba(0,0,0,0), 0px 3px 7px 0 rgba(0,0,0,0.09)",
                    px: "16px",
                    py: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: isMobile ? "100%" : "406px",
                      mx: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Typography
                          sx={{
                            fontFamily: '"Red Hat Display", sans-serif',
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#141414",
                          }}
                        >
                          Full Name
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Enter Full Name"
                          value={reminderForm.fullName}
                          onChange={handleReminderInputChange("fullName")}
                          sx={reminderFieldSx}
                        />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Typography
                          sx={{
                            fontFamily: '"Red Hat Display", sans-serif',
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#141414",
                          }}
                        >
                          Contact number
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Enter Contact number"
                          value={reminderForm.contactNumber}
                          onChange={handleReminderInputChange("contactNumber")}
                          type="tel"
                          inputProps={{ inputMode: "numeric", maxLength: 10 }}
                          sx={reminderFieldSx}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <Typography
                        sx={{
                          fontFamily: '"Red Hat Display", sans-serif',
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#141414",
                        }}
                      >
                        When Should We Remind You ?
                      </Typography>
                      <DatePicker
                        value={reminderDate}
                        onChange={(newValue) => setReminderDate(newValue)}
                        format="DD/MM/YYYY"
                        minDate={dayjs().startOf("day")}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            placeholder: "dd/mm/yyyy",
                            sx: reminderFieldSx,
                          },
                          openPickerIcon: {
                            sx: { color: "#686866" },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Button
                  fullWidth
                  onClick={handleSetReminder}
                  disabled={isReminderSubmitting}
                  sx={{
                    maxWidth: "430px",
                    width: "100%",
                    height: isMobile ? "46px" : "48px",
                    borderRadius: "10px",
                    px: isMobile ? 2 : "26.044px",
                    py: isMobile ? 1.5 : "16.278px",
                    bgcolor: "#F04951",
                    color: "#FFFFFF",
                    boxShadow: isMobile ? "0px 4px 0px #141414" : "0px 4.341px 0px #141414",
                    textTransform: "none",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 700,
                    fontSize: "14px",
                    lineHeight: "14px",
                    "&:hover": { bgcolor: "#D63F46" },
                  }}
                >
                  {isReminderSubmitting ? "Saving..." : "Set My Reminder"}
                </Button>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "360px",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    gap: isMobile ? "10px" : "12px",
                    py: isMobile ? 1 : 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: 700,
                      color: "#0a0a23",
                      lineHeight: 1.1,
                    }}
                  >
                    Reminder Set
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: isMobile ? "13px" : "14px",
                      fontWeight: 500,
                      color: "#686866",
                      lineHeight: 1.4,
                    }}
                  >
                    Reminder set successfully. Our team will contact you.
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  onClick={handleReminderSuccessClose}
                  sx={{
                    maxWidth: "240px",
                    height: "44px",
                    borderRadius: "10px",
                    bgcolor: "#1D1D1B",
                    color: "#FFFFFF",
                    boxShadow: "0px 4px 0px #B9B9B8",
                    textTransform: "none",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 700,
                    fontSize: "14px",
                    "&:hover": { bgcolor: "#2A2A28" },
                  }}
                >
                  Close
                </Button>
              </>
            )}
          </Box>
          </LocalizationProvider>
        </Box>
      </Modal>
    </>
  );
};

export default PaymentFailurePage;

